import React from 'react';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import { getContext } from 'common';
import Label, { LabelProps } from 'components/Label';
import Checkbox, { CheckboxLabelContent, CheckboxProps } from 'components/Checkbox';
import Styled from './styles';

const PADDING = isSelectAllPresent => isSelectAllPresent ? 0 : '0.875rem';

const TITLE_STYLE = (isRTL, isSelectAllPresent) => ({
  fontWeight: Number(Tokens.FONT_WEIGHT.FONT_WEIGHT_BOLD), // TODO: FIX THE TOKEN UXR-23220
  fontFamily: Tokens.FONT_FAMILY.NOKIA_PURE_TEXT_BOLD,
  paddingTop: '0.125rem',
  paddingLeft: isRTL ? 0 : PADDING(isSelectAllPresent),
  paddingRight: isRTL ? PADDING(isSelectAllPresent) : 0,
  margin: 0,
});

export interface MultiSelectSmartListHeaderProps extends React.ComponentPropsWithRef<'div'> {
  /** Text displayed as title. */
  title: string;
  /** Select all checkbox configuration. */
  selectAllCheckbox?: {
    /** Set to true to enable the select/deselect all feature. */
    isEnabled?: boolean;
    /** If `true`, the select all checkbox is checked. */
    checked?: boolean;
    /** If `true`, the select all checkbox will be rendered with indeterminate visuals. */
    indeterminate?: boolean;
    /** Callback fired when the state of the select all checkbox is changed. */
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    /** Override `Checkbox` props. */
    checkboxProps?: Partial<CheckboxProps>;
    /** Override `Label` props for checkbox label. */
    labelProps?: Partial<LabelProps>;
  };
}

/** Any other props than 'MultiSelectSmartListHeader' props will be provided to the root element. */
const MultiSelectSmartListHeader = React.forwardRef<HTMLElement, MultiSelectSmartListHeaderProps>((props: MultiSelectSmartListHeaderProps, ref) => getContext(({ RTL, theme }) => {
  const {
    title,
    selectAllCheckbox,
    ...otherProps
  } = props;
  const {
    isEnabled,
    checked,
    indeterminate,
    onChange,
    checkboxProps,
    labelProps
  } = selectAllCheckbox;
  const {
    style: labelStyle,
    ...otherLabelProps
  } = labelProps || {
    style: {}
  };

  return (
    <Styled.SmartListTitle ref={ref} RTL={RTL} $theme={theme.LIST.VERTICAL} {...otherProps}>
      <Label style={{ padding: RTL ? '0 0.125rem 0 0' : '0 0 0 0.125rem', ...labelStyle }} {...otherLabelProps}>
        {isEnabled &&
          <Styled.SmartListItemCheckbox rtl={RTL}>
            <Checkbox
              checked={checked}
              indeterminate={indeterminate}
              onChange={onChange}
              {...checkboxProps}
            />
          </Styled.SmartListItemCheckbox>
        }
        <CheckboxLabelContent style={TITLE_STYLE(RTL, isEnabled)}>{title}</CheckboxLabelContent>
      </Label>
    </Styled.SmartListTitle>
  );
}));

MultiSelectSmartListHeader.displayName = 'MultiSelectSmartListHeader';

MultiSelectSmartListHeader.defaultProps = {
  selectAllCheckbox: {
    isEnabled: false,
    checked: false,
    indeterminate: false,
    onChange: undefined,
    checkboxProps: {},
    labelProps: {
      style: {}
    }
  }
};

export default MultiSelectSmartListHeader;
